import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { MapGeoJSONFeature } from "maplibre-gl";

import { usePutAnalysisWidgetConfig } from "@/api/widget/useWidgetAPI";
import { AddWidgetIcon, LookupIcon } from "@/assets/icons";
import DropdownMultiSelect, {
  DROPDOWN_ALL_VALUE,
  IMultiOptionItem
} from "@/components/ui/dropdownMultiSelect";
import { MESSAGE } from "@/constants/message";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import useWidgetTableView from "@/hooks/dataset-widget/useWidgetTableView";
import { useMap } from "@/hooks/map/core/useMapContext";
import { findClickableFeature } from "@/hooks/map/interactions/helpers";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { useWindowSize } from "@/hooks/ui/useWindowSize";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import { RootState, store } from "@/store";
import {
  addGridSelected,
  setGridsSelected
} from "@/store/slice/analysisSummary";
import { removeSingleWidget } from "@/store/slice/widgetSlice";
import { DatasetData } from "@/types/dataset";
import {
  getWidgetGridBreakpoint,
  getWidgetSectionLayout,
  groupWidgetsBySection,
  mapWidgetsToPutAnalysisConfig
} from "@/utils/widget";

import DatasetWidgetCard from "../../map/DatasetWidgetPopup/DatasetWidgetCard";
import WidgetConfiguration from "../../map/DatasetWidgetPopup/widgetConfiguration";
import DatasetSection from "./datasetSection";

export const MAX_GRIDS_SELECTED = 3; // Maximum number of grids that can be selected
export const GRID_ID_ATTRIBUTE = "Grid ID"; // Attribute name for Grid ID in the dataset
export const CATCHMENT_ID_ATTRIBUTE = "Catchment ID"; // Attribute name for Catchment ID in the dataset
export const POLYGON_ID_ATTRIBUTE = "Polygon ID"; // Attribute name for Polygon ID in the dataset

const extractGridId = (feature: MapGeoJSONFeature): string | undefined =>
  feature.properties?.[GRID_ID_ATTRIBUTE] ??
  feature.properties?.[CATCHMENT_ID_ATTRIBUTE] ??
  feature.properties?.[POLYGON_ID_ATTRIBUTE];

/**
 * Grid/catchment ids only identify an analysis area when they come from the
 * analysis result dataset itself. Any other dataset can carry a column with the
 * same name — e.g. one saved from a previous analysis via "Save as Dataset" —
 * so clicking its feature would otherwise select an unrelated grid. Sublayers
 * are named `${datasetId}-<sublayer>` (see SUBLAYERS in @/constants/map).
 */
const belongsToDataset = (feature: MapGeoJSONFeature, datasetId: string) =>
  feature.layer.id.startsWith(datasetId);

export type AnalysisAreaData = {
  id: string;
  dataset_name: string;
  rows: { values: { value: string }[] }[];
  columns: { name: string }[];
  score: string;
};

export interface GridOptionItem extends IMultiOptionItem {
  score: number;
}
interface FilterByAnalysisAreaProps {
  dynamicChartRef: React.RefObject<(HTMLDivElement | null)[]>;
  isExpanded?: boolean;
  isFetching?: boolean;
}

const getContainerClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    if (isAllSelected) {
      return "flex flex-row 2xl:w-[28%]";
    }
    if (gridsSelected.length === 1) {
      return "flex flex-col md:flex-row 2xl:w-[28%]";
    }
    return "min-[1366px]:flex min-[1366px]:flex-row gap-3 2xl:w-[34%]";
  }

  let className;
  if (isAllSelected) {
    className = "flex flex-row";
  } else if (gridsSelected.length === 1) {
    className = "flex";
  } else {
    className = "flex flex-col";
  }

  return className;
};

const getDropdownClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    return isAllSelected || gridsSelected.length === 1 ? "w-full" : "";
  }
  return "w-full";
};

const getScoreContainerClassName = (
  isExpanded: boolean,
  isAllSelected: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    if (isAllSelected) {
      return "items-center justify-between flex-row overflow-auto";
    }
    if (gridsSelected.length === 1) {
      return "items-center justify-between flex-row overflow-auto mt-0 md:mt-2";
    }
    return "min-[1366px]:flex-row 2xl:w-3/4 gap-3 justify-between min-[1366px]:items-center";
  }

  if (isAllSelected || gridsSelected.length === 1) {
    return "items-center justify-between flex-row";
  }

  return "flex flex-col mt-0";
};

const getGridContainerClassName = (
  isExpanded: boolean,
  gridsSelected: string[]
) => {
  if (isExpanded) {
    return "min-[1366px]:flex-row justify-between transition-all min-[1366px]:justify-end min-[1366px]:w-full gap-2 xl:space-x-8 xl:w-3/5 overflow-x-auto";
  }
  if (gridsSelected.length === 1) {
    return "";
  }
  return "mt-2 ";
};

const FilterByAnalysisArea = ({
  dynamicChartRef,
  isExpanded = false,
  isFetching = false
}: FilterByAnalysisAreaProps) => {
  const dispatch = useDispatch();
  const { isDatasetWidgetEnabled } = useAppFeatures();
  const [showWidgetConfigModal, setShowWidgetConfigModal] = useState(false);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { gridsSelected, spatialAnalysisDatasetId } = useSelector(
    (state: RootState) => state.analysisSummary
  );
  // Updated synchronously during render so effects always read the current value

  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const { widgets, visualizationOrder } = useSelector(
    (state: RootState) => state.widget
  );
  const { width } = useWindowSize();
  const breakpoint = useMemo(() => getWidgetGridBreakpoint(width), [width]);

  const { map, handleHighlightGridSelection } = useMap();
  const { mutate: persistAnalysisWidgetConfig, isPending: isRemovePending } =
    usePutAnalysisWidgetConfig();
  const { showConfirmation } = useConfirmationModal();
  const { isTableView, toggleTableView, resetTableView } = useWidgetTableView();

  const {
    gridValue,
    isAllSelected,
    originalDatasets,
    getTypeSummary,
    runCategoryNormalizers
  } = useAnalysisSummaryDataContext();

  const orderedWidget = useMemo(
    () => visualizationOrder.map((id) => widgets[id]).filter(Boolean),
    [visualizationOrder, widgets]
  );

  const widgetSections = useMemo(
    () => groupWidgetsBySection(orderedWidget),
    [orderedWidget]
  );

  const widgetIndexMap = useMemo(
    () => Object.fromEntries(orderedWidget.map((w, i) => [w.widget_id, i])),
    [orderedWidget]
  );

  const containerClassName = getContainerClassName(
    isExpanded,
    isAllSelected,
    gridsSelected
  );

  const dropdownClassName = getDropdownClassName(
    isExpanded,
    isAllSelected,
    gridsSelected
  );

  const handleSelectGrid = (values: string[]) => {
    let resolvedGrids: string[] = [];

    if (values.length > 0) {
      resolvedGrids = values;
    } else if (gridValue?.length > 0) {
      resolvedGrids = [gridValue[0].value];
    }

    dispatch(setGridsSelected(resolvedGrids));

    if (resolvedGrids[0] === DROPDOWN_ALL_VALUE) {
      runCategoryNormalizers();
    }
    if (latestAnalysisId && isDatasetWidgetEnabled) {
      persistAnalysisWidgetConfig({
        analysisId: latestAnalysisId,
        payload: mapWidgetsToPutAnalysisConfig(
          Object.values(widgets),
          resolvedGrids
        )
      });
    }

    // No highlight call here: `setGridsSelected` always stores a new array, so the
    // effect below re-runs on the change and applies it. Calling it here as well
    // ran the highlight over the whole dataset twice per selection.
  };

  const handleOpenWidgetConfig = useCallback((id: string) => {
    setSelectedWidgetId(id);
    setShowWidgetConfigModal(true);
    setIsEdit(true);
  }, []);

  const handleCloseWidgetConfig = useCallback(() => {
    setSelectedWidgetId(null);
    setShowWidgetConfigModal(false);
  }, []);

  const handleRemoveWidget = useCallback(
    (id: string) => {
      showConfirmation({
        title: MESSAGE.WIDGET_REMOVE_CONFIRMATION_TITLE,
        message: MESSAGE.WIDGET_REMOVE_CONFIRMATION_MESSAGE,
        confirmLabel: MESSAGE.WIDGET_REMOVE_CONFIRM_LABEL,
        cancelLabel: "Cancel",
        onConfirm: () => {
          if (!latestAnalysisId) {
            dispatch(removeSingleWidget(id));
            resetTableView(id);
            return;
          }

          const nextWidgetList = Object.values(widgets).filter(
            (w) => w.widget_id !== id
          );

          persistAnalysisWidgetConfig(
            {
              analysisId: latestAnalysisId,
              payload: mapWidgetsToPutAnalysisConfig(
                nextWidgetList,
                gridsSelected
              )
            },
            {
              onSuccess: () => {
                dispatch(removeSingleWidget(id));
                resetTableView(id);
              },
              onError: () =>
                showToast({
                  type: EToastType.ERROR,
                  message: MESSAGE.WIDGET_REMOVE_ERROR_MESSAGE
                })
            }
          );
        }
      });
    },
    [
      showConfirmation,
      latestAnalysisId,
      widgets,
      persistAnalysisWidgetConfig,
      gridsSelected,
      dispatch,
      resetTableView
    ]
  );

  // Applying the highlight once per selection change is enough: the feature
  // state is stored per source, not per tile, so MapLibre re-applies it to every
  // tile as it loads. The previous `sourcedata` listener re-ran the whole
  // highlight on every tile of every source — hundreds of times during a single
  // pan — because the old tile-based read could only see loaded tiles.
  useEffect(() => {
    if (!map || !spatialAnalysisDatasetId) return;
    handleHighlightGridSelection(spatialAnalysisDatasetId, gridsSelected);
  }, [
    map,
    spatialAnalysisDatasetId,
    gridsSelected,
    handleHighlightGridSelection
  ]);

  // FIX ME: move to independent hook
  // Register Analysis Summary-specific map interaction handler.
  // This is a short-term solution until proper refactoring is done because the order of this registration matters and must happen after the clickHandler registration in useMapInteractions.tsx
  useEffect(() => {
    if (!map || !spatialAnalysisDatasetId) return;

    const handleMapClick = (e: maplibregl.MapMouseEvent) => {
      // Only handle grid selection while the analysis summary popup is open.
      // Read fresh on each click since this handler is registered once per map.
      if (!store.getState().ui.showAnalysisSummaryPopup) return;

      const features = map.queryRenderedFeatures(e.point);
      const clickedFeature = findClickableFeature(features);
      if (
        !clickedFeature ||
        !belongsToDataset(clickedFeature, spatialAnalysisDatasetId)
      ) {
        return;
      }

      const gridOrCatchmentId = extractGridId(clickedFeature);
      if (!gridOrCatchmentId) return;

      if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
        // Ctrl/Cmd+Click: toggle this area in the current multi-selection
        dispatch(addGridSelected(gridOrCatchmentId));
      } else {
        // Plain click: exclusive select
        dispatch(setGridsSelected([gridOrCatchmentId]));
      }

      // The dispatch above is synchronous, so gridsSelected read back from the
      // store here already includes this click.
      const { analysisSummary, spatial, widget } = store.getState();
      if (spatial.latestAnalysisId && isDatasetWidgetEnabled) {
        persistAnalysisWidgetConfig({
          analysisId: spatial.latestAnalysisId,
          payload: mapWidgetsToPutAnalysisConfig(
            Object.values(widget.widgets),
            analysisSummary.gridsSelected
          )
        });
      }
    };

    map.on("click", handleMapClick);
    return () => {
      map.off("click", handleMapClick);
    };
  }, [
    dispatch,
    isDatasetWidgetEnabled,
    map,
    persistAnalysisWidgetConfig,
    spatialAnalysisDatasetId
  ]);

  // FIX ME: move to independent hook
  // Handle Shift + Click for selected grid by boundingBox when analysisSummaryPopup 'true'
  useEffect(() => {
    if (!map || !spatialAnalysisDatasetId) return;

    let start: { x: number; y: number } | null = null;
    let box: HTMLDivElement | null = null;

    const onMouseDown = (e: maplibregl.MapMouseEvent) => {
      if (!e.originalEvent.shiftKey) return;
      e.originalEvent.preventDefault();
      map.dragPan.disable();
      start = { x: e.point.x, y: e.point.y };
      box = document.createElement("div");
      Object.assign(box.style, {
        position: "absolute",
        border: "2px dashed #14b8a6",
        background: "rgba(20,184,166,0.15)",
        pointerEvents: "none"
      });
      map.getContainer().appendChild(box);
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!start || !box) return;
      const { x, y } = e.point;
      Object.assign(box.style, {
        left: `${Math.min(start.x, x)}px`,
        top: `${Math.min(start.y, y)}px`,
        width: `${Math.abs(x - start.x)}px`,
        height: `${Math.abs(y - start.y)}px`
      });
    };

    const onMouseUp = (e: maplibregl.MapMouseEvent) => {
      if (!start) return;
      map.dragPan.enable();
      box?.remove();
      box = null;

      const { x, y } = e.point;
      if (Math.abs(x - start.x) > 4 || Math.abs(y - start.y) > 4) {
        const ids = [
          ...new Set(
            map
              .queryRenderedFeatures([
                [start.x, start.y],
                [x, y]
              ])
              .filter((feature) =>
                belongsToDataset(feature, spatialAnalysisDatasetId)
              )
              .map(extractGridId)
              .filter((id): id is string => !!id)
          )
        ];
        if (ids.length > 0) {
          dispatch(setGridsSelected(ids));
          const { analysisSummary, spatial, widget } = store.getState();
          if (spatial.latestAnalysisId && isDatasetWidgetEnabled) {
            persistAnalysisWidgetConfig({
              analysisId: spatial.latestAnalysisId,
              payload: mapWidgetsToPutAnalysisConfig(
                Object.values(widget.widgets),
                analysisSummary.gridsSelected
              )
            });
          }
        }
      }

      start = null;
    };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    map.on("mouseup", onMouseUp);

    return () => {
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
      box?.remove();
      map.dragPan.enable();
    };
  }, [
    dispatch,
    isDatasetWidgetEnabled,
    map,
    persistAnalysisWidgetConfig,
    spatialAnalysisDatasetId
  ]);

  // FIX ME: move to independent hook
  // Handle unsubscribe boxZoom keyboard shortcut form native maplibre-gl when analysisSummaryPopup 'true'
  useEffect(() => {
    if (!map) return;

    // MapLibre's boxZoom handler also activates on shift+drag independently of
    // our custom drag-selection. Without disabling it, both run simultaneously:
    // MapLibre draws its own zoom rectangle on top of ours and zooms the map on
    // mouseup instead of selecting grids. We disable boxZoom for the duration
    // of the analysis summary popup and restore it when the popup closes.
    const syncBoxZoom = () => {
      // Read fresh state on every store update — this runs as a subscriber, so
      // capturing showAnalysisSummaryPopup once at mount would ignore changes.
      const { showAnalysisSummaryPopup } = store.getState().ui;
      if (showAnalysisSummaryPopup) {
        map.boxZoom.disable();
      } else {
        map.boxZoom.enable();
      }
    };

    syncBoxZoom();
    const unsubscribe = store.subscribe(syncBoxZoom);

    return () => {
      unsubscribe();
      map.boxZoom.enable();
    };
  }, [map]);

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-txtHeading">
            {isDatasetWidgetEnabled ? "Filter Area" : "Filter by Analysis Area"}
          </span>
          {isDatasetWidgetEnabled && (
            <button
              onClick={() => {
                setShowWidgetConfigModal(!showWidgetConfigModal);
                setIsEdit(false);
              }}
              className="flex justify-between items-center gap-2 text-teal-500 text-sm font-semibold"
              disabled={isDatasetWidgetEnabled && isFetching}
              type="button"
            >
              <AddWidgetIcon
                className={"fill-teal-500"}
                title="add widget icon"
              />
              Add Widget
            </button>
          )}
        </div>
        <div
          aria-label="filter-by-analysis-area"
          className={cn("gap-3 items-center", containerClassName)}
        >
          <DropdownMultiSelect
            className={cn("mt-3 h-14", dropdownClassName)}
            buttonClassName="py-4 h-[52px]"
            name="gridID/catchmentID/polygonID"
            placeholder="GridID/CatchmentID/PolygonID"
            data={gridValue}
            labelBorder={""}
            onSelect={handleSelectGrid}
            defaultValue={gridsSelected}
            showSelectAll
            selectAllSuffix="N/A"
            maxSelected={
              isDatasetWidgetEnabled ? undefined : MAX_GRIDS_SELECTED
            }
            selectedCountPlaceHolder={`${getTypeSummary().toLowerCase()} selected`}
            disabled={isDatasetWidgetEnabled && isFetching}
          />
          {!isDatasetWidgetEnabled && (
            <div
              className={cn(
                "px-3 py-2 w-full flex bg-teal-50 border border-teal-500 rounded-xl flex-col mt-2",
                getScoreContainerClassName(
                  isExpanded,
                  isAllSelected,
                  gridsSelected
                )
              )}
            >
              <div className="flex gap-2 w-[80px]">
                <LookupIcon className="w-5 h-5 fill-teal-500" />
                <span className="text-sm font-semibold">Score</span>
              </div>
              {isAllSelected ? (
                <div className="flex flex-col">
                  <span className="text-[10px] font-semibold text-teal-500">
                    {getTypeSummary()}ID: All
                  </span>
                  <span className="text-sm font-semibold text-teal-500">
                    N/A
                  </span>
                </div>
              ) : (
                <div
                  className={cn(
                    "flex justify-between",
                    getGridContainerClassName(isExpanded, gridsSelected)
                  )}
                >
                  {gridsSelected.map((grid) => {
                    const gridData = gridValue.find(
                      (item) => item.value === grid
                    );
                    if (!gridData) return null;
                    return (
                      <div
                        key={gridData.value}
                        className={cn(
                          "flex flex-col",
                          gridsSelected.length === 2 && "w-1/2"
                        )}
                      >
                        <span className="text-[10px] font-semibold text-teal-500">
                          {getTypeSummary()}ID: {gridData.value}
                        </span>
                        <span className="text-sm font-semibold text-teal-500">
                          {(gridData.score ?? 0).toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {!isDatasetWidgetEnabled && originalDatasets.length > 0 && (
        <div
          className={cn(isExpanded ? "columns-1 2xl:columns-4 mt-4" : "mt-3")}
        >
          {originalDatasets.map((dataset: DatasetData, index: number) => (
            <DatasetSection
              key={dataset.dataset_id}
              isExpanded={isExpanded}
              dataset={dataset}
              ref={(el) => {
                if (dynamicChartRef.current)
                  dynamicChartRef.current[index] = el;
              }}
            />
          ))}
        </div>
      )}
      {isDatasetWidgetEnabled &&
        (isExpanded ? (
          <div className="flex flex-col gap-4 mt-4">
            {widgetSections.map((section, sectionIndex) => {
              const { cols, emptySlots } = getWidgetSectionLayout(
                section,
                breakpoint
              );
              return (
                <div
                  key={`${section.type}-${sectionIndex}`}
                  data-testid={`widget-grid-section-${section.type}`}
                  className="grid gap-4"
                  style={{
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`
                  }}
                >
                  {section.widgets.map((widget) => {
                    const globalIndex = widgetIndexMap[widget.widget_id];
                    return (
                      <div
                        key={widget.widget_id}
                        ref={(el) => {
                          if (
                            dynamicChartRef.current &&
                            globalIndex !== undefined
                          )
                            dynamicChartRef.current[globalIndex] = el;
                        }}
                      >
                        <DatasetWidgetCard
                          widget={widget}
                          analysisId={latestAnalysisId}
                          isTableView={isTableView(widget.widget_id)}
                          onToggleTableView={() =>
                            toggleTableView(widget.widget_id)
                          }
                          onOpenWidgetConfig={handleOpenWidgetConfig}
                          onRemoveWidget={handleRemoveWidget}
                          isRemovePending={isRemovePending}
                        />
                      </div>
                    );
                  })}
                  {Array.from({ length: emptySlots }, (_, i) => (
                    <div key={`empty-${i}`} data-testid="widget-empty-slot" />
                  ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-3 mt-3">
            {orderedWidget.map((widget, index) => (
              <div
                key={widget.widget_id}
                ref={(el) => {
                  if (dynamicChartRef.current)
                    dynamicChartRef.current[index] = el;
                }}
              >
                <DatasetWidgetCard
                  widget={widget}
                  analysisId={latestAnalysisId}
                  isTableView={isTableView(widget.widget_id)}
                  onToggleTableView={() => toggleTableView(widget.widget_id)}
                  onOpenWidgetConfig={handleOpenWidgetConfig}
                  onRemoveWidget={handleRemoveWidget}
                  isRemovePending={isRemovePending}
                />
              </div>
            ))}
          </div>
        ))}
      {isDatasetWidgetEnabled && showWidgetConfigModal && (
        <WidgetConfiguration
          open={showWidgetConfigModal}
          onClose={handleCloseWidgetConfig}
          isEditing={isEdit}
          selectedWidgetId={selectedWidgetId}
          analysisId={latestAnalysisId}
          onWidgetUpdated={resetTableView}
        />
      )}
    </>
  );
};

export default FilterByAnalysisArea;
